import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import donnees from "@/data/diapo.json";
import onde from "@/data/onde.json";
import { BandeEmail } from "@/components/CarteDuJour";

// Le héros : le concept en une phrase, un diaporama d'histoires vraies en images (vignettes vidéo
// de 3,6 s, chargées à la demande), le « concept en 55 secondes » (audio, voix IA d'Alexandre + extraits
// de vrais épisodes) et le champ e-mail. Mêmes classes (carte.css, préfixe cj-) que scripts/prerendu.js.
const { concept, slides } = donnees;
const DELAI_MS = 4200;
const mm = (s) => `${Math.floor(Math.round(s) / 60)}:${String(Math.round(s) % 60).padStart(2, "0")}`;
const calme = () =>
    typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* ---------- le diaporama ---------- */
const Diapo = ({ force }) => {
    const [i, setI] = useState(0);
    const [pret, setPret] = useState(false); // la page est chargée : on peut aller chercher la suite
    const [pause, setPause] = useState(() => calme());
    const [survol, setSurvol] = useState(false);
    const [caché, setCaché] = useState(false);
    const [eco] = useState(() => typeof navigator !== "undefined" && !!(navigator.connection && navigator.connection.saveData));
    const [vus, setVus] = useState(() => new Set([0])); // vignettes déjà demandées : on ne les décharge plus
    const videos = useRef({});

    useEffect(() => {
        const go = () => setTimeout(() => setPret(true), 1200);
        let t = null;
        if (document.readyState === "complete") t = go();
        else {
            const f = () => {
                t = go();
            };
            window.addEventListener("load", f, { once: true });
            return () => window.removeEventListener("load", f);
        }
        return () => clearTimeout(t);
    }, []);

    useEffect(() => {
        const f = () => setCaché(document.hidden);
        document.addEventListener("visibilitychange", f);
        return () => document.removeEventListener("visibilitychange", f);
    }, []);

    // L'audio du concept pilote les images ; sinon, le diaporama avance seul.
    useEffect(() => {
        if (force === null || force === undefined) return;
        const k = slides.findIndex((s) => s.id === force);
        if (k >= 0) setI(k);
    }, [force]);

    const auto = pret && !pause && !survol && !caché && (force === null || force === undefined);
    useEffect(() => {
        if (!auto) return undefined;
        const t = setTimeout(() => setI((x) => (x + 1) % slides.length), DELAI_MS);
        return () => clearTimeout(t);
    }, [auto, i]);

    // La vignette active tourne ; les autres s'arrêtent.
    useEffect(() => {
        Object.entries(videos.current).forEach(([k, el]) => {
            if (!el) return;
            if (Number(k) === i && pret && !eco && !calme() && !caché) {
                el.currentTime = 0;
                const p = el.play();
                if (p && p.catch) p.catch(() => {});
            } else el.pause();
        });
    }, [i, pret, eco, caché]);

    const s = slides[i];
    const suivante = (i + 1) % slides.length;
    useEffect(() => {
        if (!pret) return;
        setVus((v) => (v.has(i) && v.has(suivante) ? v : new Set([...v, i, suivante])));
    }, [pret, i, suivante]);
    const chargees = (k) => vus.has(k);
    return (
        <div
            className="cj-diapo"
            role="group"
            aria-roledescription="diaporama"
            aria-label="Des histoires vraies en images"
            onMouseEnter={() => setSurvol(true)}
            onMouseLeave={() => setSurvol(false)}
            onFocus={() => setSurvol(true)}
            onBlur={() => setSurvol(false)}
            data-testid="hero-diapo"
        >
            <span className="cj-pince" aria-hidden="true" />
            <span className="cj-stamp" aria-hidden="true">VRAI</span>
            <div className="cj-dia-carte">
                <div className="cj-dia-cadre">
                    {slides.map((d, k) =>
                        chargees(k) ? (
                            <figure className="cj-dia" data-actif={k === i} key={d.id} aria-hidden={k === i ? undefined : "true"}>
                                <img
                                    src={`/media/diapo-${d.id}.webp`}
                                    width="400"
                                    height="500"
                                    alt=""
                                    decoding="async"
                                    {...(k === 0 ? { fetchPriority: "high" } : {})}
                                />
                                {pret && !eco && !calme() ? (
                                    <video
                                        ref={(el) => {
                                            videos.current[k] = el;
                                        }}
                                        src={`/media/diapo-${d.id}.mp4`}
                                        muted
                                        loop
                                        playsInline
                                        preload="auto"
                                        aria-hidden="true"
                                        tabIndex={-1}
                                    />
                                ) : null}
                            </figure>
                        ) : null,
                    )}
                    <div className="cj-dia-ctrl">
                        <div className="cj-dia-pts" role="group" aria-label="Choisir une histoire">
                            {slides.map((d, k) => (
                                <button
                                    type="button"
                                    key={d.id}
                                    className="cj-dia-pt"
                                    aria-current={k === i ? "true" : undefined}
                                    aria-label={`Histoire ${k + 1} sur ${slides.length} : ${d.titre}`}
                                    onClick={() => setI(k)}
                                />
                            ))}
                        </div>
                        <button
                            type="button"
                            className="cj-dia-pause"
                            onClick={() => setPause((p) => !p)}
                            aria-pressed={pause}
                            aria-label={pause ? "Relancer le diaporama" : "Mettre le diaporama en pause"}
                            data-testid="hero-diapo-pause"
                        >
                            {pause ? "▶" : "❚❚"}
                        </button>
                    </div>
                </div>
                <div className="cj-dia-leg">
                    <span className="cj-lab cj-dia-dom">{s.domaine}</span>
                    <Link className="cj-dia-titre" to={`/histoires/${s.slug}`} data-testid="hero-diapo-titre">
                        {s.titre}
                    </Link>
                </div>
            </div>
        </div>
    );
};

/* ---------- le lecteur du concept : bouton + onde précalculée ---------- */
const BRIQUE = onde.traits.length;

// Le dessin de l'onde (même balisage que scripts/prerendu.js). `p` : part déjà jouée (0 → 1).
export const Onde = ({ p, courant }) => (
    <svg className="cj-onde-svg" viewBox={`0 0 ${onde.l} ${onde.h}`} preserveAspectRatio="none" aria-hidden="true" focusable="false">
        <defs>
            <linearGradient id="cj-onde-g" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2={onde.l} y2="0">
                <stop offset={p} stopColor="#b4421f" />
                <stop offset={p} stopColor="#17251d" />
            </linearGradient>
        </defs>
        {onde.traits.map((t, k) => (
            <line key={k} x1={t[0]} y1={t[1]} x2={t[2]} y2={t[3]} stroke="url(#cj-onde-g)" className={k === courant ? "b" : undefined} />
        ))}
    </svg>
);

const Lecteur = ({ onTemps }) => {
    const audio = useRef(null);
    const onde_ = useRef(null);
    const [joue, setJoue] = useState(false);
    const [t, setT] = useState(0);
    const [dureeAudio, setDuree] = useState(onde.duree);
    const [glisse, setGlisse] = useState(null); // part visée pendant un glisser (0 → 1)

    useEffect(
        () => () => {
            if (audio.current) audio.current.pause();
        },
        [],
    );

    const obtenir = () => {
        if (!audio.current) {
            const a = new Audio(concept.mp3);
            a.preload = "auto";
            a.addEventListener("timeupdate", () => {
                setT(a.currentTime);
                onTemps(a.currentTime);
            });
            a.addEventListener("loadedmetadata", () => a.duration && isFinite(a.duration) && setDuree(a.duration));
            a.addEventListener("play", () => setJoue(true));
            a.addEventListener("pause", () => {
                setJoue(false);
                onTemps(-1); // le diaporama reprend sa marche seul
            });
            a.addEventListener("ended", () => {
                setJoue(false);
                setT(0);
                onTemps(-1);
            });
            audio.current = a;
        }
        return audio.current;
    };
    const lancer = (a) => {
        const p = a.play();
        if (p && p.catch) p.catch(() => setJoue(false));
    };
    const basculer = () => {
        const a = obtenir();
        if (a.paused) lancer(a);
        else a.pause();
    };
    const aller = (secondes, jouer) => {
        const a = obtenir();
        const d = (a.duration && isFinite(a.duration) ? a.duration : dureeAudio) || 1;
        const v = Math.max(0, Math.min(d - 0.05, secondes));
        a.currentTime = v;
        setT(v);
        if (jouer && a.paused) lancer(a);
    };

    // Clic ou glisser sur l'onde : on vise une position, on l'applique au relâchement.
    const part = (e) => {
        const r = onde_.current.getBoundingClientRect();
        return Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
    };
    const surOnde = {
        onPointerDown: (e) => {
            if (e.button !== undefined && e.button !== 0) return;
            e.currentTarget.setPointerCapture(e.pointerId);
            setGlisse(part(e));
        },
        onPointerMove: (e) => {
            if (glisse !== null) setGlisse(part(e));
        },
        onPointerUp: (e) => {
            if (glisse === null) return;
            const f = part(e);
            setGlisse(null);
            aller(f * dureeAudio, true);
        },
        onPointerCancel: () => setGlisse(null),
        onKeyDown: (e) => {
            const pas = { ArrowRight: 5, ArrowUp: 5, ArrowLeft: -5, ArrowDown: -5, PageUp: 10, PageDown: -10 }[e.key];
            if (pas !== undefined) aller(t + pas, false);
            else if (e.key === "Home") aller(0, false);
            else if (e.key === "End") aller(dureeAudio, false);
            else return;
            e.preventDefault();
        },
    };

    const p = glisse !== null ? glisse : Math.min(1, t / dureeAudio);
    const courant = joue || glisse !== null ? Math.min(BRIQUE - 1, Math.floor(p * BRIQUE)) : -1;
    const demarre = joue || t > 0;
    const reste = Math.max(0, dureeAudio - p * dureeAudio);
    return (
        <div className="cj-ecoute-bloc cj-lecteur" data-testid="concept-lecteur">
            <button
                type="button"
                className="cj-lec-play"
                onClick={basculer}
                aria-pressed={joue}
                aria-label={joue ? "Mettre le concept en pause" : demarre ? "Reprendre le concept" : "Écouter le concept en 55 secondes"}
                data-testid="concept-play"
                data-joue={joue}
            >
                {joue ? (
                    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="M6 4h4v16H6zM14 4h4v16h-4z" fill="currentColor" /></svg>
                ) : (
                    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="M7 4l13 8-13 8z" fill="currentColor" /></svg>
                )}
            </button>
            <div
                className="cj-onde"
                ref={onde_}
                role="slider"
                tabIndex={0}
                aria-label="Position dans le concept"
                aria-valuemin={0}
                aria-valuemax={Math.round(dureeAudio)}
                aria-valuenow={Math.round(p * dureeAudio)}
                aria-valuetext={`${mm(p * dureeAudio)} sur ${mm(dureeAudio)}`}
                data-testid="concept-onde"
                {...surOnde}
            >
                <Onde p={p} courant={courant} />
            </div>
            <span className="cj-lec-reste" data-testid="concept-reste" aria-hidden="true">
                {demarre ? `−${mm(reste)}` : mm(dureeAudio)}
            </span>
            <p className="cj-lec-legende">
                <b>Écouter le concept</b>
                <span className="cj-main cj-ec-note" aria-hidden="true">avec la voix IA d&apos;Alexandre</span>
            </p>
        </div>
    );
};

export const Hero = () => {
    const [t, setT] = useState(-1);
    let force = null;
    if (t >= 0) for (const c of concept.cues) if (t >= c.t) force = c.slide;
    const mail = t >= concept.mail_t;
    return (
        <section id="promesse" className="cj-w cj-hero" aria-labelledby="cj-h1" data-moment={mail ? "mail" : undefined}>
            <div className="cj-hero-grille">
                <div className="cj-hero-texte">
                    <h1 id="cj-h1" data-testid="hero-title">
                        Chaque matin, une histoire vraie <span>qui aiguise l&apos;esprit, en 90 secondes.</span>
                    </h1>
                    <p className="cj-sub">
                        Chaque jour, un fait vrai et étonnant, vérifié à partir de sources sérieuses. Tu le lis ou
                        tu l&apos;écoutes en moins de deux minutes.
                    </p>
                    <Lecteur onTemps={setT} />
                    <details className="cj-transcr">
                        <summary>Lire la transcription</summary>
                        <p>{concept.transcription}</p>
                    </details>
                    <BandeEmail testid="cta-haut" id="email-haut" sansPhrase />
                </div>
                <Diapo force={force} />
            </div>
        </section>
    );
};
